import moment from "moment";
import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Badge,
  Button,
  OverlayTrigger,
  Table,
  Tooltip
} from "react-bootstrap";
import { withTranslation } from "react-i18next";
import {
  BiSort,
  BiChevronUp,
  BiChevronDown,
  BiPencil,
  BiSolidHourglass,
  BiX, BiCheck, BiMinus
} from "react-icons/bi";
import { BsEye, BsPerson, BsCheckCircle } from "react-icons/bs";
import { FcFinePrint } from "react-icons/fc";

import Pagination from "../../../../components/Pagination/Pagination";
import { LoaderContext } from "../../../../App";
import {
  DEFAULT_ITEMS_PER_PAGE,
  FORM_BUILDER_LISTING_URL,
  FORM_BUILDER_REVIEW_URL,
  LS_ACCESS_TOKEN_KEY,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC,
  STATUS
} from "../../../../core/constants/common";
import FormService from "../../../../core/service/FormService";
import Utils from "../../../../core/utilities/Utils";
import "../Forms.scss";

function ApprovalsTab({ t, id, self, currentRoute, searchKey, filters = {}, activeTab }) {
  const { setLoaderDisplay } = useContext(LoaderContext);
  const authToken = localStorage.getItem(LS_ACCESS_TOKEN_KEY);

  const [itemsTotalCount, setItemTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [order, setOrder] = useState(SORT_ORDER_ASC);
  const [sortField, setSortField] = useState(self === 'true' ? 'parentBranch' : 'status');
  const [forms, setForms] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [logsData, setLogsData] = useState({});
  const [loadingLogs, setLoadingLogs] = useState({});
  const isInitialMount = useRef(true);
  const prevFiltersRef = useRef(null);
  const prevSearchKeyRef = useRef(null);
  const prevActiveTabRef = useRef(null);

  // Reset expanded rows when filters or searchKey changes
  useEffect(() => {
    setExpandedRows({});
  }, [filters.childStatus, searchKey]);

  // Combine effects to prevent duplicate calls
  useEffect(() => {
    if (!id) return;

    const isTabActive = activeTab === 'approvals';
    const wasTabActive = prevActiveTabRef.current === 'approvals';
    const tabBecameActive = !wasTabActive && isTabActive;

    // On initial mount or when tab becomes active, fetch data
    if (isInitialMount.current || tabBecameActive) {
      isInitialMount.current = false;
      prevActiveTabRef.current = activeTab;
      prevFiltersRef.current = filters.childStatus;
      prevSearchKeyRef.current = searchKey;
      if (isTabActive) {
        getSubmissions(0, itemsPerPage, searchKey, sortField);
      }
      return;
    }

    // Update prevActiveTabRef even if tab is not active
    prevActiveTabRef.current = activeTab;

    // Only refetch if tab is active and filters or searchKey changed
    if (!isTabActive) return;

    // Check if filters or searchKey actually changed
    const filtersChanged = prevFiltersRef.current !== filters.childStatus;
    const searchKeyChanged = prevSearchKeyRef.current !== searchKey;

    prevFiltersRef.current = filters.childStatus;
    prevSearchKeyRef.current = searchKey;

    // Refetch only if something actually changed
    if (filtersChanged || searchKeyChanged) {
      setCurrentPage(1);
      getSubmissions(0, itemsPerPage, searchKey, sortField);
    }
  }, [id, searchKey, filters.childStatus, activeTab]);

  const getSubmissions = (pageNumber, itemsPerPageVal, key = "", sortField) => {
    setLoaderDisplay(true);
    let perPage = '';
    if (pageNumber > 0) {
      pageNumber = pageNumber - 1;
    } else {
      setCurrentPage(1);
    }
    if (itemsPerPageVal > 0) {
      perPage = itemsPerPageVal;
    } else {
      perPage = itemsPerPage;
    }
    let sort = sortField + "," + order;

    // Build status filter from childStatusFilters
    let statusParam = null;
    if (filters.childStatus) {
      statusParam = filters.childStatus;
    }

    FormService.getSubmissions(
      id,
      pageNumber,
      perPage,
      sort,
      key,
      true, // toReview = true for Approvals tab
      statusParam
    ).then((response) => {
      const totalPages = parseFloat(response.headers["x-total-count"]);
      setItemTotalCount(totalPages);
      setForms([...response.data]);
      setLoaderDisplay(false);
    })
      .catch((error) => {
        setLoaderDisplay(false);
      });
  };

  const handleSort = (val) => {
    const newOrder = sortField === val && order === "asc" ? "desc" : "asc";
    setOrder(newOrder);
    setSortField(val);
    getSubmissions(0, itemsPerPage, searchKey, val);
  };

  const viewSubmission = (action, formId = "", sId, key) => {
    const formType = "ASSESSMENT";
    const string = Utils.generateUrlParamFormCreate(
      action,
      authToken,
      formType,
      0,
      formId,
      key,
      sId,
      null,
      { route: currentRoute, qParams: { tab: 'app' } }
    );
    const encodedDat = Utils.encodeBase64(string);
    window.location.href = FORM_BUILDER_LISTING_URL + `/?q=${encodedDat}`;
  };

  const handleApproval = (action, formId = "", sId, key) => {
    const formType = "ASSESSMENT";
    const string = Utils.generateUrlParamFormCreate(
      action,
      authToken,
      formType,
      0,
      formId,
      key,
      sId,
      null,
      { route: currentRoute, qParams: { tab: 'app' } }
    );
    const encodedDat = Utils.encodeBase64(string);
    window.location.href = FORM_BUILDER_REVIEW_URL + `/?q=${encodedDat}`;
  };

  const renderStatusBadge = (item) => {
    if (item.tokenStatus === 'REVOKED') {
      return (
        <Badge bg="badge-Rejected">
          {STATUS['NO_PERMISSION']}
        </Badge>
      );
    }
    switch (item.status) {
      case "IN_PROGRESS":
        return (
          <Badge bg="badge-Draft">
            {STATUS[item.status]}
          </Badge>
        );
      case "COMPLETED":
        return (
          <Badge bg="badge-Published">
            {STATUS[item.status]}
          </Badge>
        );
      case "DRAFT":
        return (
          <Badge bg="badge-New">
            {STATUS['NOT_STARTED']}
          </Badge>
        );
      default:
        return (
          <Badge bg="badge-New">
            {STATUS[item.status]}
          </Badge>
        );
    }
  };

  const renderDataOwners = (emails) => {
    if (!emails) return '';
    const emailString = Array.isArray(emails) ? emails.join(',') : emails.toString();
    if (!emailString.trim()) return '';

    const emailList = emailString.split(',').map(email => email.trim()).filter(email => email);

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0px', alignItems: 'center' }}>
        {emailList.map((email, idx) => (
          <span key={idx}>
            <span style={{ display: 'inline-block' }}>{email}</span>
            {idx < emailList.length - 1 && <span style={{ marginLeft: '1px', marginRight: '4px' }}>,</span>}
          </span>
        ))}
      </div>
    );
  };

  const toggleRowExpansion = async (rowId, formDataId) => {
    const isCurrentlyExpanded = expandedRows[rowId];

    // If expanding and logs not loaded yet, fetch them
    if (!isCurrentlyExpanded && formDataId && !logsData[rowId]) {
      setLoaderDisplay(true);
      try {
        const response = await FormService.getFormDataLogs(formDataId);

        if (response && response.status === 200) {
          // Handle response.data - it should be an array
          let logsArray = [];
          if (Array.isArray(response.data)) {
            logsArray = response.data;
          } else if (response.data && Array.isArray(response.data.data)) {
            // In case response is wrapped
            logsArray = response.data.data;
          } else if (response.data) {
            // If it's a single object, wrap it in an array
            logsArray = [response.data];
          }

          if (logsArray.length > 0) {
            setLogsData(prev => ({
              ...prev,
              [rowId]: logsArray
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching logs:', error);
        if (error.response) {
          console.error('Error response:', error.response.data);
        }
      } finally {
        setLoaderDisplay(false);
      }
    }

    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const mapLogToWorkflowHistory = (log) => {
    let status = log.newStatus || '';
    const approvalLevel = Number(log.approvalLevel);
    if (status === 'PENDING' && (approvalLevel === 3 || approvalLevel === 2)) {
      status = 'PENDING_APPROVAL';
    }
    let statusLabel = STATUS[status] || status;

    // Map status labels for approval levels
    // Handle PENDING_APPROVAL with approval level
    if (status === 'PENDING_APPROVAL' && log.approvalLevel) {
      statusLabel = t(`PendingApprovalLevel${log.approvalLevel}.label`) || `Pending Approval at Level ${log.approvalLevel}`;
    }
    // Handle PENDING status with approval level (levels other than 1/2)
    else if (status === 'PENDING' && log.approvalLevel) {
      statusLabel = t(`PendingApprovalLevel${log.approvalLevel}.label`) || `Pending Approval at Level ${log.approvalLevel}`;
    }
    // Handle PENDING_APPROVAL_LEVEL_X statuses
    else if (status.startsWith('PENDING_APPROVAL_LEVEL_')) {
      const level = status.replace('PENDING_APPROVAL_LEVEL_', '');
      statusLabel = t(`PendingApprovalLevel${level}.label`) || `Pending Approval at Level ${level}`;
    }
    // Handle PENDING status without level (fallback)
    else if (status === 'PENDING') {
      statusLabel = t("Pending.label") || "Pending";
    }
    // Handle APPROVED with approval level
    else if (status === 'APPROVED' && log.approvalLevel) {
      statusLabel = t(`ApprovedApprovalLevel${log.approvalLevel}.label`) || `Approved at Level ${log.approvalLevel}`;
    }
    // Handle APPROVED_LEVEL_X statuses
    else if (status.startsWith('APPROVED_LEVEL_')) {
      const level = status.replace('APPROVED_LEVEL_', '');
      statusLabel = t(`ApprovedApprovalLevel${level}.label`) || `Approved at Level ${level}`;
    }
    // Handle APPROVED status without level (fallback)
    else if (status === 'APPROVED') {
      statusLabel = t("Approved.label") || "Approved";
    }
    // Handle REJECTED with approval level
    else if (status === 'REJECTED' && log.approvalLevel) {
      statusLabel = t(`RejectedApprovalLevel${log.approvalLevel}.label`) || `Rejected at Level ${log.approvalLevel}`;
    }
    // Handle REJECTED_LEVEL_X statuses
    else if (status.startsWith('REJECTED_LEVEL_')) {
      const level = status.replace('REJECTED_LEVEL_', '');
      statusLabel = t(`RejectedApprovalLevel${level}.label`) || `Rejected at Level ${level}`;
    }
    // Handle REJECTED status without level (fallback)
    else if (status === 'REJECTED') {
      statusLabel = t("Rejected.label") || "Rejected";
    }

    const isPendingApproval =
      status === 'PENDING_APPROVAL' || status.startsWith('PENDING_APPROVAL_LEVEL_');

    let userName = '';
    let userEmail = '';
    if (isPendingApproval) {
      if (log.approver) {
        userEmail = log.approver.email || '';
        if (log.approver.fullName) {
          userName = log.approver.fullName;
        } else if (userEmail) {
          userName = userEmail;
        }
      }
      if (!userEmail) {
        userEmail = log.approverEmail || log.changedBy?.email || log.changedByEmail || '';
      }
      if (!userName) {
        if (log.changedBy?.fullName) {
          userName = log.changedBy.fullName;
        } else if (userEmail) {
          userName = userEmail;
        } else if (log.changedByEmail) {
          userName = log.changedByEmail;
        } else {
          userName = log.createdBy || 'System';
        }
      }
    } else if (log.changedBy && log.changedBy.fullName) {
      userName = log.changedBy.fullName;
      userEmail = log.changedBy.email || log.changedByEmail || '';
    } else if (log.changedBy && log.changedBy.email) {
      userName = log.changedBy.email;
      userEmail = log.changedBy.email;
    } else if (log.changedByEmail) {
      userName = log.changedByEmail;
      userEmail = log.changedByEmail;
    } else {
      userName = log.createdBy || 'System';
    }

    return {
      status: status,
      user: userName,
      userEmail,
      date: log.changedAt || log.createdDate,
      statusLabel: statusLabel,
      changeReason: log.changeReason || '',
      approvalLevel: log.approvalLevel
    };
  };

  const renderWorkflowHistory = (item, rowId) => {
    const logs = logsData[rowId];
    const isLoading = loadingLogs[rowId];

    if (isLoading) {
      return (
        <div className="workflow-history-container">
          <div className="text-center p-3">
            <span>{t("Loading.label") || "Loading..."}</span>
          </div>
        </div>
      );
    }

    if (!logs || !Array.isArray(logs) || logs.length === 0) {
      return (
        <div className="workflow-history-container">
          <div className="text-center p-3">
            <span>{t("NoLogsFound.label") || "No logs found"}</span>
          </div>
        </div>
      );
    }

    // Sort logs by date descending (most recent first)
    const sortedLogs = [...logs].sort((a, b) => {
      const dateA = new Date(a.changedAt || a.createdDate);
      const dateB = new Date(b.changedAt || b.createdDate);
      return dateB - dateA;
    });

    let workflowHistory = sortedLogs.map(mapLogToWorkflowHistory);

    // Filter out PENDING status logs
    workflowHistory = workflowHistory.filter(step => step.status !== 'PENDING');

    // Consolidate consecutive IN_PROGRESS entries - keep only the latest one
    const consolidatedHistory = [];
    for (let i = 0; i < workflowHistory.length; i++) {
      const currentStep = workflowHistory[i];

      // If current step is IN_PROGRESS and previous step was also IN_PROGRESS, skip it
      if (currentStep.status === 'IN_PROGRESS' &&
        i > 0 &&
        workflowHistory[i - 1].status === 'IN_PROGRESS') {
        continue;
      }

      consolidatedHistory.push(currentStep);
    }

    workflowHistory = consolidatedHistory;

    return (
      <div className="workflow-history-container">
        {workflowHistory.map((step, index) => (
          <div key={index} className="workflow-step">
            <div className="workflow-step-content">
              <div className="workflow-step-indicator">
                {step.status === 'COMPLETED' ? (
                  <div className="workflow-indicator badge bg-badge-Published">
                    <BiCheck size={16} />
                  </div>
                ) : step.status === 'APPROVED' ? (
                  <div className="workflow-indicator badge bg-badge-Approved">
                    <BiCheck size={15} />
                  </div>
                ) : step.status === 'REJECTED' ? (
                  <div className="workflow-indicator badge bg-badge-Rejected">
                    <BiX size={15} />
                  </div>
                ) : step.status === 'CORRECTION_PENDING' || step.status === 'CORRECTED' ? (
                  <div className="workflow-indicator badge bg-badge-Corrected">
                    <BiPencil className="workflow-icon-pencil" />
                  </div>
                ) : step.status === 'PENDING' ||
                  step.status === 'PENDING_APPROVAL' ||
                  step.status === 'PENDING_APPROVAL_LEVEL_1' ||
                  step.status === 'PENDING_APPROVAL_LEVEL_2' ||
                  step.status === 'PENDING_APPROVAL_LEVEL_3' ||
                  step.status === 'PENDING_APPROVAL_LEVEL_4' ||
                  step.status === 'PENDING_APPROVAL_LEVEL_5' ? (
                  <div className="workflow-indicator badge bg-badge-Accepted">
                    <BiSolidHourglass />
                  </div>
                ) : (
                  <div className="workflow-indicator badge bg-badge-Draft">
                    <BiMinus size={15} />
                  </div>
                )}
              </div>
              <div className="workflow-step-details">
                <Badge
                  bg={
                    (step.status === 'COMPLETED') ? 'badge-Published' :
                      step.status === 'APPROVED' ? 'badge-Approved' :
                        step.status === 'REJECTED' ? 'badge-Rejected' :
                          step.status === 'CORRECTION_PENDING' || step.status === 'CORRECTED' ? 'badge-Corrected' :
                            step.status === 'DRAFT' ? 'badge-New' :
                              step.status === 'IN_PROGRESS' ? 'badge-Draft' :
                                step.status === 'PENDING' ||
                                  step.status === 'PENDING_APPROVAL' ||
                                  step.status === 'PENDING_APPROVAL_LEVEL_1' ||
                                  step.status === 'PENDING_APPROVAL_LEVEL_2' ||
                                  step.status === 'PENDING_APPROVAL_LEVEL_3' ||
                                  step.status === 'PENDING_APPROVAL_LEVEL_4' ||
                                  step.status === 'PENDING_APPROVAL_LEVEL_5' ? 'badge-Accepted' :
                                  'badge-Invited'
                  }
                  className="rounded-pill workflow-status-badge"
                >
                  {step.statusLabel}
                </Badge>

                <div className="workflow-step-meta">
                  <div className="workflow-step-meta">
                    <BsPerson className="workflow-user-icon" />
                    {step.userEmail && step.userEmail !== step.user ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip id={`workflow-user-tooltip-${index}`}>{step.userEmail}</Tooltip>}
                      >
                        <span className="workflow-user-name">{step.user}</span>
                      </OverlayTrigger>
                    ) : (
                      <span className="workflow-user-name">{step.user}</span>
                    )}
                    <span className="workflow-date">
                      {moment(step.date).format('MMM DD YYYY, h:mm a')}
                    </span>
                  </div>
                  {step.changeReason && (
                    <div className="workflow-change-reason">
                      <div>{step.changeReason}</div>
                    </div>
                  )}
                </div>

              </div>
            </div>
            {index < workflowHistory.length - 1 && (
              <div className="workflow-connector"></div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderStatusBadgeWithDropdown = (item) => {
    const currentStatus = item.status || 'PENDING_APPROVAL';
    const rowId = item.id;
    let statusLabel = STATUS[currentStatus] || currentStatus;

    // Map status labels for approval levels
    if (currentStatus === 'PENDING_APPROVAL') {
      const approvalLevel = item.pendingApprovalLevel;
      statusLabel = approvalLevel
        ? t(`PendingApprovalLevel${approvalLevel}.label`) || `Pending Approval at Level ${approvalLevel}`
        : t("PendingApproval.label");
    } else if (currentStatus === 'DRAFT') {
      statusLabel = t("NotStarted.label") || 'Not Started';
    }

    // Determine badge color based on status
    let badgeColor = 'badge-Invited';
    if (currentStatus === 'CORRECTION_PENDING' || currentStatus === 'CORRECTED') {
      badgeColor = 'badge-Corrected';
    } else if (currentStatus === 'REJECTED') {
      badgeColor = 'badge-Rejected';
    } else if (currentStatus === 'COMPLETED' || currentStatus === 'APPROVED') {
      badgeColor = 'badge-Published';
    } else if (currentStatus === 'DRAFT') {
      badgeColor = 'badge-New';
    } else if (currentStatus === 'IN_PROGRESS') {
      badgeColor = 'badge-Draft';
    } else if (currentStatus === 'PENDING_APPROVAL' ||
      currentStatus === 'PENDING_APPROVAL_LEVEL_1' ||
      currentStatus === 'PENDING_APPROVAL_LEVEL_2' ||
      currentStatus === 'PENDING_APPROVAL_LEVEL_3') {
      badgeColor = 'badge-Accepted';
    }

    return (
      <div className="status-badge-container">
        <Badge
          bg={badgeColor}
          className="rounded-pill status-badge-clickable"
          onClick={() => toggleRowExpansion(rowId, item.id)}
        >
          {statusLabel}
          {expandedRows[rowId] ? (
            <BiChevronUp className="ms-1" />
          ) : (
            <BiChevronDown className="ms-1" />
          )}
        </Badge>
      </div>
    );
  };

  // Filter data based on search key
  const filteredData = forms.filter((item) => {
    if (!searchKey) return true;
    const searchLower = searchKey.toLowerCase();
    const branchName = self === 'true' ? item.branch?.name : item.submittedByCompany?.name;
    return branchName?.toLowerCase().includes(searchLower);
  });

  return (
    <div className="List_Table_01">
      <div className="card List_table-card01">
        <Table responsive>
          <thead>
            <tr>
              {self === 'true' ? (
                <>
                  <th className="white-nowrap">
                    <a 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleSort("parentBranch");
                      }}
                    >
                      {t("ParentEntity.label") || "Parent Entity"}
                      <BiSort />
                    </a>
                  </th>
                  <th className="white-nowrap">
                    <a 
                      href="#" 
                      onClick={(e) => {
                        e.preventDefault();
                        handleSort("branch");
                      }}
                    >
                      {t("ChildEntity.label")}
                      <BiSort />
                    </a>
                  </th>
                  <th className="white-nowrap">{t("DataOwners.label")}</th>
                </>
              ) : (
                <th className="white-nowrap">{t("Supplier.label")}</th>
              )}
              <th className="white-nowrap">{t("SharedDate.label")}</th>
              <th className="white-nowrap">
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    handleSort("submissionDate");
                  }}
                >
                  {t("SubmittedDate.label")}
                  <BiSort />
                </a>
              </th>
              <th className="white-nowrap">
                {/* <Link to="#" onClick={() => handleSort("status")}>
                  {t("Status.label")}
                  <BiSort />
                </Link> */}
                {t("Status.label")}
              </th>
              <th className="white-nowrap"></th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((item) => {
                const rowId = item.id;
                const isExpanded = expandedRows[rowId];
                return (
                  <React.Fragment key={rowId}>
                    <tr>
                      {self === 'true' ? (
                        <>
                          <td>
                            {item.branch?.parent
                              ? item.branch.parent.name
                              : item.branch?.name || '-'}
                          </td>
                          <td>
                            {item.branch?.parent
                              ? item.branch.name
                              : '-'}
                          </td>
                          <td>{renderDataOwners(item.branch?.emails)}</td>
                        </>
                      ) : (
                        <td>{item.submittedByCompany?.name}</td>
                      )}
                      <td className="white-nowrap">
                        {item.assessment?.sharedOn
                          ? moment(item.assessment?.sharedOn).format("MMM DD, YYYY")
                          : ''}
                      </td>
                      <td>
                        <div className="white-nowrap">
                          {item.submissionDate
                            ? moment(item.submissionDate).format("MMM DD, YYYY, h:mm a")
                            : "-"}
                        </div>
                      </td>
                      <td>{renderStatusBadgeWithDropdown(item)}</td>
                      <td width="125px">
                        {(!item.formReviewRequest || item.formReviewRequest.status != 'PENDING') && (
                          <OverlayTrigger
                            placement="auto"
                            overlay={<Tooltip id="tooltip-view">{t("ViewResponses.label")}</Tooltip>}
                          >
                            <Button
                              variant="blank_outline"
                              className="ms-2 view-icon-btn"
                              onClick={() => viewSubmission(
                                "SUBMIT",
                                item.form?.id,
                                item.submissionId,
                                item.token
                              )}
                            >
                              <BsEye />
                            </Button>
                          </OverlayTrigger>
                        )}
                        {item.formReviewRequest && item.formReviewRequest.status == 'PENDING' &&(
                          <OverlayTrigger
                            placement="auto"
                            overlay={<Tooltip id="tooltip-approval">{t("ApprovalsTab.approvalTooltip.label")}</Tooltip>}
                          >
                            <Button
                              variant="primary"
                              className="ms-2 btn-sm"
                              onClick={() => handleApproval(
                                "REVIEW",
                                item.form.id,
                                item.submissionId,
                                item.token
                              )}
                            >
                              <BsCheckCircle /> {t("Approve.label") || "Approve"}
                            </Button>
                          </OverlayTrigger>
                        )}
                      </td>
                    </tr>
                    {/* Expanded Workflow History Row */}
                    {isExpanded && (
                      <tr className="workflow-history-row">
                        <td colSpan={self === 'true' ? "7" : "6"} className="workflow-history-cell">
                          {renderWorkflowHistory(item, rowId)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={self === 'true' ? "7" : "6"} className="text-center">
                  <div className="Nodatafound_box">
                    <h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <div className="p-2 List_footer_count">
          <Pagination
            itemsTotalCount={itemsTotalCount}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            alwaysShown={true}
            setItemsPerPage={setItemsPerPage}
            pageClicked={(pageNum, perPage) => {
              getSubmissions(pageNum, perPage, searchKey, sortField);
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default withTranslation()(ApprovalsTab);
